import {
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    tableRowClasses,
} from '@mui/material';
import Column from '../../../../components/Column';
import { TableCellText, TablePrimaryCell } from '../../../../components/Metrics/MetricTable';
import { absChangeRatio, changeCode, formatNumber, formatPercent, ratio, subtract } from '../../HatUtils';
import { attributionExplainTooltip } from '../../HatDashboardPage';
import { HatChangeMetricWithSkeleton } from './HatChangeMetricWithSkeleton';
import { useHatDbContext } from '../../context/HatDbContext';
import { HatMetricChannelAttributionData } from '../dashboard/HatDbSectionChannelAttribution';

// Width of skeleton placeholders in the table.
const skeletonWidth = 40;

export function HatDbVisualChannelAttributionTable(props: {
    currentData: HatMetricChannelAttributionData;
    previousData: HatMetricChannelAttributionData;
}) {
    const { currentData, previousData } = props;

    const hatDbContext = useHatDbContext();
    const hasPreviousInterval = !!hatDbContext.getPreviousRangeCriteria();

    const channelData = currentData
        .map((currentChannel) => {
            // Get the matching channel's data for the previous interval if available.
            const previousChannel = previousData.find((v) => v.sourceCategory === currentChannel.sourceCategory);

            return {
                sourceCategory: currentChannel.sourceCategory,
                current: {
                    count: currentChannel?.count,
                    adInfluencedCount: currentChannel?.adInfluencedCount,
                },
                previous: {
                    count: previousChannel?.count,
                    adInfluencedCount: previousChannel?.adInfluencedCount,
                },
            };
        })
        // Sort rows in descending order.
        .sort((a, b) => (a.current.adInfluencedCount ?? 0) - (a.current.adInfluencedCount ?? 0));

    return (
        <TableContainer
            sx={{
                borderCollapse: 'inherit',
                borderColor: (theme) => theme.palette.grey[300],
                borderStyle: 'solid',
                borderWidth: 1,
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            <Table
                sx={{
                    [`& .${tableRowClasses.root} .${tableCellClasses.root}`]: {
                        width: '25%',
                        textAlign: 'right',
                    },

                    [`& .${tableRowClasses.root} .${tableCellClasses.root}:nth-of-type(1)`]: {
                        textAlign: 'left',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Channel</TableCell>
                        <TableCell>Total Visits</TableCell>
                        <TableCell>
                            <TableCellText tooltip={attributionExplainTooltip}>Attributed Visits</TableCellText>
                        </TableCell>
                        <TableCell>% Attributed Visits</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {channelData.map((channel) => (
                        <TableRow key={channel.sourceCategory}>
                            <TablePrimaryCell>{channel.sourceCategory}</TablePrimaryCell>
                            <TableCell>
                                <Column gap={0} alignItems="flex-end">
                                    <HatChangeMetricWithSkeleton
                                        value={channel.current.count}
                                        format={formatNumber}
                                        skeleton={skeletonWidth}
                                    />

                                    <HatChangeMetricWithSkeleton
                                        value={absChangeRatio(channel.current.count, channel.previous.count)}
                                        format={formatPercent}
                                        changeCode={changeCode(channel.current.count, channel.previous.count)}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={0} alignItems="flex-end">
                                    <HatChangeMetricWithSkeleton
                                        value={channel.current.adInfluencedCount}
                                        format={formatNumber}
                                        skeleton={skeletonWidth}
                                    />

                                    <HatChangeMetricWithSkeleton
                                        value={absChangeRatio(
                                            channel.current.adInfluencedCount,
                                            channel.previous.adInfluencedCount
                                        )}
                                        format={formatPercent}
                                        changeCode={changeCode(
                                            channel.current.adInfluencedCount,
                                            channel.previous.adInfluencedCount
                                        )}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={0} alignItems="flex-end">
                                    <HatChangeMetricWithSkeleton
                                        value={ratio(channel.current.adInfluencedCount, channel.current.count)}
                                        format={formatPercent}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />

                                    <HatChangeMetricWithSkeleton
                                        value={subtract(
                                            ratio(channel.current.adInfluencedCount, channel.current.count),
                                            ratio(channel.previous.adInfluencedCount, channel.previous.count)
                                        )}
                                        format={formatPercent}
                                        changeCode={changeCode(
                                            ratio(channel.current.adInfluencedCount, channel.current.count),
                                            ratio(channel.previous.adInfluencedCount, channel.previous.count)
                                        )}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />
                                </Column>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TableCell>Total</TableCell>
                        <TableCell>
                            <Column gap={0} alignItems="flex-end">
                                <HatChangeMetricWithSkeleton
                                    value={currentData.total?.count}
                                    format={formatNumber}
                                    skeleton={skeletonWidth}
                                />

                                <HatChangeMetricWithSkeleton
                                    value={absChangeRatio(currentData.total?.count, previousData.total?.count)}
                                    format={formatPercent}
                                    changeCode={changeCode(currentData.total?.count, previousData.total?.count)}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />
                            </Column>
                        </TableCell>
                        <TableCell>
                            <Column gap={0} alignItems="flex-end">
                                <HatChangeMetricWithSkeleton
                                    value={currentData.total?.adInfluencedCount}
                                    format={formatNumber}
                                    skeleton={skeletonWidth}
                                />

                                <HatChangeMetricWithSkeleton
                                    value={absChangeRatio(
                                        currentData.total?.adInfluencedCount,
                                        previousData.total?.adInfluencedCount
                                    )}
                                    format={formatPercent}
                                    changeCode={changeCode(
                                        currentData.total?.adInfluencedCount,
                                        previousData.total?.adInfluencedCount
                                    )}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />
                            </Column>
                        </TableCell>

                        <TableCell>
                            <Column gap={0} alignItems="flex-end">
                                <HatChangeMetricWithSkeleton
                                    value={ratio(currentData.total?.adInfluencedCount, currentData.total?.count)}
                                    format={formatPercent}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />

                                <HatChangeMetricWithSkeleton
                                    value={subtract(
                                        ratio(currentData.total?.adInfluencedCount, currentData.total?.count),
                                        ratio(previousData.total?.adInfluencedCount, previousData.total?.count)
                                    )}
                                    format={formatPercent}
                                    changeCode={changeCode(
                                        ratio(currentData.total?.adInfluencedCount, currentData.total?.count),
                                        ratio(previousData.total?.adInfluencedCount, previousData.total?.count)
                                    )}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />
                            </Column>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
}
